// components/auth/ForgotPasswordDialog.tsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { apiHandler } from '../../utils/api/responseHandler';
import config from '../../config';
import logger from '../../utils/logging/logger';

const componentName = 'ForgotPasswordDialog';

interface ForgotPasswordDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    onResetLinkSent?: (email: string) => void;
}

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
    open,
    onClose,
    onSuccess,
    onResetLinkSent
}) => {
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Username validation states
    const [isValidatingUsername, setIsValidatingUsername] = useState(false);
    const [usernameError, setUsernameError] = useState('');
    const [usernameValid, setUsernameValid] = useState(false);
    const [hasBlurredUsername, setHasBlurredUsername] = useState(false);

    useEffect(() => {
        if (open) {
            // Reset form state when dialog opens
            setUserName('');
            setEmail('');
            setError('');
            setSuccess(false);
            setIsSubmitting(false);
            // Reset username validation states
            setIsValidatingUsername(false);
            setUsernameError('');
            setUsernameValid(false);
            setHasBlurredUsername(false);
        }
    }, [open]);

    // Validate username with server
    const validateUsername = async (username: string) => {
        if (!username.trim()) {
            setUsernameError('');
            setUsernameValid(false);
            return;
        }

        setIsValidatingUsername(true);
        setUsernameError('');

        try {
            logger.log('LogSpecificTarget', componentName, 'Validating username:', { userName: username });

            const response = await apiHandler.fetch<{ exists: boolean; message?: string }>(
                `${config.API_URL}/api/user/validate-username`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userName: username }),
                    validateFn: (data) => data
                }
            );

            if (response.error) {
                logger.logError('LogError', componentName, 'Failed to validate username', { userName: username });
                setUsernameError('Unable to validate username');
                setUsernameValid(false);
                return;
            }

            if (!response.data?.exists) {
                setUsernameError('Username not found');
                setUsernameValid(false);
            } else {
                setUsernameError('');
                setUsernameValid(true);
            }

        } catch (err) {
            logger.logError('LogError', componentName, 'Error validating username', err);
            setUsernameError('Unable to validate username');
            setUsernameValid(false);
        } finally {
            setIsValidatingUsername(false);
        }
    };

    // Handle username blur event
    const handleUsernameBlur = () => {
        setHasBlurredUsername(true);
        validateUsername(userName);
    };

    // Handle username change
    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setUserName(value);

        // Clear validation states when user types
        if (hasBlurredUsername) {
            setUsernameError('');
            setUsernameValid(false);
        }
    };

    const handleSubmit = async () => {
        // Validate inputs
        if (!userName.trim() || !email.trim()) {
            setError('Please enter both username and email address');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Check if username validation failed
        if (hasBlurredUsername && !usernameValid && !usernameError.includes('Unable to validate')) {
            setError('Please enter a valid username');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            logger.log('LogSpecificTarget', componentName, 'Requesting password reset token via - /api/user/reset-token:', { userName, email });

            const response = await apiHandler.fetch<{ success: boolean; message: string }>(
                `${config.API_URL}/api/user/reset-token`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userName, email }),
                    validateFn: (data) => data
                }
            );

            if (response.error) {
                logger.logError('LogError', componentName, 'Failed to request password reset', { userName, email });
                throw new Error(response.error.message || 'Failed to request password reset');
            }

            if (!response.data?.success) {
                logger.logError('LogError', componentName, 'Failed to send reset email. Please check your information and try again.', { userName, email });
                throw new Error('Failed to send reset email. Please check your information and try again.');
            }

            logger.log('LogSpecificTarget', componentName, 'Password reset token created successfully');
            setSuccess(true);

            if (onResetLinkSent) {
                onResetLinkSent(email);
            }

            // Close dialog after success message is shown for a few seconds
            setTimeout(() => {
                if (onSuccess) onSuccess();
                onClose();
            }, 3000);
        } catch (err) {
            logger.logError('LogError', componentName, 'Error requesting password reset', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isSubmitting) {
            e.preventDefault();
            handleSubmit();
        }
    };

    // Determine username field state
    const getUsernameFieldState = () => {
        if (!hasBlurredUsername) return {};

        if (isValidatingUsername) {
            return {
                color: 'primary' as const
            };
        }

        if (usernameError) {
            return {
                error: true,
                helperText: usernameError
            };
        }

        if (usernameValid) {
            return {
                color: 'success' as const,
                helperText: 'Username found'
            };
        }

        return {};
    };

    // Get end adornment for username field
    const getUsernameEndAdornment = () => {
        if (!hasBlurredUsername) return null;

        if (isValidatingUsername) {
            return (
                <InputAdornment position="end">
                    <CircularProgress size={20} />
                </InputAdornment>
            );
        }

        if (usernameError) {
            return (
                <InputAdornment position="end">
                    <ErrorIcon color="error" />
                </InputAdornment>
            );
        }

        if (usernameValid) {
            return (
                <InputAdornment position="end">
                    <CheckCircle color="success" />
                </InputAdornment>
            );
        }

        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="forgot-password-dialog-title"
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="forgot-password-dialog-title">
                Forgot Password
            </DialogTitle>
            <DialogContent>
                {success ? (
                    <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                        Password reset link has been sent to your email address. Please check your inbox.
                    </Alert>
                ) : (
                    <>
                        <DialogContentText>
                            Please enter your username and email address. We will send you a link to reset your password.
                        </DialogContentText>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ mt: 2 }}>
                            <TextField
                                autoComplete="username"
                                autoFocus
                                margin="dense"
                                label="Username"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={userName}
                                onChange={handleUsernameChange}
                                onBlur={handleUsernameBlur}
                                disabled={isSubmitting}
                                onKeyDown={handleKeyDown}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    endAdornment: getUsernameEndAdornment()
                                }}
                                {...getUsernameFieldState()}
                            />
                            <TextField
                                autoComplete="email"
                                margin="dense"
                                label="Email Address"
                                type="email"
                                fullWidth
                                variant="outlined"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isSubmitting}
                                onKeyDown={handleKeyDown}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" disabled={isSubmitting}>
                    Cancel
                </Button>
                {!success && (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Send Reset Link"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ForgotPasswordDialog;